import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { faCalendarXmark } from "@fortawesome/pro-solid-svg-icons/faCalendarXmark";
import type { VisitorEvent } from "../../hooks/visitor/useVisitorEvents";
import { VisitorPageHeader } from "./VisitorPageHeader";
import { EventsBody } from "./events/EventContent";
import type { VisitorNotification } from "./notification-types";

interface EventsPageProps {
  mapSlug: string;
  logoUrl?: string;
  appName?: string;
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  shareUrl: string;
  shareTitle: string;
  shareText?: string;
  happeningNowEvents: VisitorEvent[];
  todaysEvents: VisitorEvent[];
  upcomingEvents: VisitorEvent[];
  onNavigateToPoi?: (poiId: string, event: VisitorEvent) => void;
  poiNames?: Map<string, string>;
  /** Active POI filter — when set, renders chip + empty state for invalid IDs */
  poiFilter?: { id: string; name: string; onClear: () => void };
}

export function EventsPage({
  mapSlug,
  logoUrl,
  appName,
  notifications,
  onBellClick,
  isTrayOpen,
  shareUrl,
  shareTitle,
  shareText,
  happeningNowEvents,
  todaysEvents,
  upcomingEvents,
  onNavigateToPoi,
  poiNames,
  poiFilter,
}: EventsPageProps) {
  const { t } = useTranslation("events");
  const hasAnyEvents =
    happeningNowEvents.length + todaysEvents.length + upcomingEvents.length > 0;

  return (
    <div
      className="absolute inset-0 z-10 bg-background flex flex-col"
      style={{ paddingBottom: "var(--bottom-nav-height, 0px)" }}
    >
      <VisitorPageHeader
        logoUrl={logoUrl}
        appName={appName}
        showShare
        shareUrl={shareUrl}
        shareTitle={shareTitle}
        shareText={shareText}
        notifications={notifications}
        onBellClick={onBellClick}
        isTrayOpen={isTrayOpen}
        mapSlug={mapSlug}
      />
      <div className="flex-1 overflow-y-auto">
        {poiFilter && (
          <div className="px-4 pt-3">
            <div className="flex items-center justify-between gap-2 rounded-full bg-muted px-3 py-2 text-sm">
              <span className="truncate">
                {t("filteredByPoi", { name: poiFilter.name })}
              </span>
              <button
                type="button"
                onClick={poiFilter.onClear}
                className="shrink-0 inline-flex items-center gap-1 text-primary hover:underline"
                aria-label={t("clearFilter")}
              >
                <span>{t("clearFilter")}</span>
                <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
        {poiFilter && !hasAnyEvents ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground p-8 mt-4">
            <FontAwesomeIcon icon={faCalendarXmark} className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-center mb-4">
              {t("noEventsForPoi", { name: poiFilter.name })}
            </p>
            <button
              type="button"
              onClick={poiFilter.onClear}
              className="text-primary hover:underline text-sm"
            >
              {t("viewAllEvents")}
            </button>
          </div>
        ) : (
          <EventsBody
            happeningNowEvents={happeningNowEvents}
            todaysEvents={todaysEvents}
            upcomingEvents={upcomingEvents}
            onNavigateToPoi={onNavigateToPoi}
            poiNames={poiNames}
          />
        )}
      </div>
    </div>
  );
}
